import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { Box } from "@mui/material";
import { WidgetContext } from "../../store/widgetContext";
import { getVoiceWidgetDomain } from "../../utils/common";
import { getOrigin, getWwsAssistantId } from "../../utils/widgetStorage";
import { useSession } from "../../session/useSession";


export function createUrlWithParams(baseUrl, params) {
	const url = new URL(baseUrl);
	Object.entries(params).forEach(([key, value]) => {
		if (value !== undefined && value !== null) {
			url.searchParams.append(key, value);
		}
	});
	return url.toString();
}

export default function Voice({ conversationId, handleNavigate, ...props }) {
	const { botId, tenantId, projectUUID, uuid } = props;
	const { assistant, hostName, pathName, isMuted } = useContext(WidgetContext);
	const { sessionId } = useSession();
	const [iframeUrl, setIframeUrl] = useState(null);
	const iframeRef = useRef(null);

	/**
	 * Mirror the header's mute state into the voice call.
	 *
	 * The header button already silences widget notification sounds; it now also
	 * silences the agent's voice, which is what a caller pressing "mute" expects.
	 * The call runs in a cross-origin iframe, so postMessage is the only way to reach
	 * it — and the mute is purely local playback in that frame: the call, the LLM and
	 * the TTS stream are untouched, so unmuting makes the agent audible again mid
	 * sentence rather than restarting or dropping the reply.
	 */
	const postMuteState = useCallback(() => {
		const frameWindow = iframeRef.current?.contentWindow;
		if (!frameWindow || !iframeUrl) return;
		try {
			frameWindow.postMessage(
				{ type: "intelswift:voice_mute", muted: !!isMuted },
				new URL(iframeUrl).origin,
			);
		} catch {
			/* frame torn down mid-toggle */
		}
	}, [isMuted, iframeUrl]);

	// Push on every toggle, and once the frame has loaded.
	useEffect(() => { postMuteState(); }, [postMuteState]);

	// The frame asks on mount: it can finish loading after a toggle, and the caller may
	// have muted before ever opening the call, so it must not assume "unmuted".
	useEffect(() => {
		const onFrameMessage = (event) => {
			if (event.data?.type === "intelswift:voice_mute_request") postMuteState();
		};
		window.addEventListener("message", onFrameMessage);
		return () => window.removeEventListener("message", onFrameMessage);
	}, [postMuteState]);

	useEffect(() => {
		setIframeUrl(null);

		// Delay iframe loading until header animation completes (300ms + small buffer)
		const animationDelay = setTimeout(() => {
			const assistantId = assistant?._id || getWwsAssistantId();
			const localUUID = `${uuid}_${botId}`;
			const urlData = getOrigin();
			const origin = urlData ? JSON.stringify(urlData) : (hostName && pathName ? JSON.stringify({ hostname: hostName, pathname: pathName }) : null);

			const requiredParams = {
				assistantId,
				tenantId,
				botId,
				uuid: localUUID,
				originHost: hostName,
				originPath: pathName,
				conversationId: conversationId,
			};

			// Only include origin if we don't have originHost/originPath
			if (origin && !hostName && !pathName) {
				requiredParams.origin = origin;
			}

			const requiredParamsForCheck = { ...requiredParams };
			delete requiredParamsForCheck.conversationId;

			const allParamsAvailable = Object.values(requiredParamsForCheck).every(Boolean);

			if (allParamsAvailable) {
				if (sessionId) {
					requiredParams.sessionId = sessionId;
				}
				const url = createUrlWithParams(getVoiceWidgetDomain(), requiredParams);
				setIframeUrl(url);
			} else {
				console.warn("[Voice] Missing params for iframe:", requiredParamsForCheck);
			}
		}, 350);

		return () => clearTimeout(animationDelay);
	}, [assistant?._id, tenantId, botId, hostName, pathName, conversationId, projectUUID, sessionId]);

	return (
		<Box
			sx={{
				width: "100%",
				height: "100%",
				display: "flex",
				flexDirection: "column",
				background: "white",
				borderRadius: "16px",
				overflow: "hidden",
			}}
		>
			{iframeUrl && (
				<iframe
					ref={iframeRef}
					src={iframeUrl}
					onLoad={postMuteState}
					allow="microphone *"
					style={{
						border: "none",
						width: "100%",
						height: "100%"
					}}
				/>
			)}
		</Box>
	);
}
